import {InaccessibleInfo, makeInaccessibleInfo} from 'auth/RequestAccess';
import {ParameterStructuredTableRowMetadata} from 'notebook/integrations/procedures/ParameterStructuredTableAdapter';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {getUpdatedCellWithLSUV} from 'notebook/structured-tables/row-updaters/getUpdatedCellWithLSUV';
import {
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
  StructuredTableRowUpdaterModel,
} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType} from 'notebook/tables/TableConstants';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';

export const ParameterStructuredTableRowUpdater: StructuredTableRowUpdater<
  StructuredTableRowUpdaterModel,
  ParameterStructuredTableRowMetadata
> = {
  /**
   * The parameter structured table does not support table refreshes. The `updateRow` method is only invoked
   * by the table adapter for Data Mod to track the number of cells being changed, which also enables the
   * submission of any audit logs based on the differences from the edits.
   */
  updateRow(
    model: StructuredTableRowUpdaterModel | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<ParameterStructuredTableRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<ParameterStructuredTableRowMetadata> {
    const cells = row.cells.map((cell, columnIndex) => {
      const column = columns[columnIndex];
      if (
        column.columnType === TableColumnType.ROW_DELEGATE &&
        row.metadata.delegatedColumns &&
        !column.isReadOnly
      ) {
        const columnProvenanceUuidOnCell = row.metadata.delegatedColumns[column.provenanceUuid];
        return getUpdatedCellWithLSUV(columnProvenanceUuidOnCell, cell, row.lastSyncedUpstreamValues);
      } else if (!column.isReadOnly) {
        return getUpdatedCellWithLSUV(column.provenanceUuid, cell, row.lastSyncedUpstreamValues);
      } else {
        return cell;
      }
    });

    return {
      ...row,
      cells,
    };
  },

  fetchModel(sampleLookupInfo: SampleLookupInfo): Promise<InaccessibleInfo> {
    return Promise.resolve(makeInaccessibleInfo(sampleLookupInfo.idForLookup));
  },

  bulkFetchModel(sampleLookupInfos: Array<SampleLookupInfo>): Promise<Array<InaccessibleInfo>> {
    return Promise.resolve(sampleLookupInfos.map((info) => makeInaccessibleInfo(info.idForLookup)));
  },

  readFromCache: () => null,

  updateCell: () => 'NO_VALUE',
};
